import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

export const CURRENCIES = [
  { code: 'EUR', symbol: '€', name: 'Euro', locale: 'fr-FR', flag: '🇪🇺' },
  { code: 'USD', symbol: '$', name: 'US Dollar', locale: 'en-US', flag: '🇺🇸' },
  { code: 'GBP', symbol: '£', name: 'British Pound', locale: 'en-GB', flag: '🇬🇧' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar', locale: 'en-CA', flag: '🇨🇦' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', locale: 'de-CH', flag: '🇨🇭' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', locale: 'ja-JP', flag: '🇯🇵' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', locale: 'zh-CN', flag: '🇨🇳' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', locale: 'en-AU', flag: '🇦🇺' },
  { code: 'MAD', symbol: 'DH', name: 'Moroccan Dirham', locale: 'fr-MA', flag: '🇲🇦' },
  { code: 'XAF', symbol: 'FCFA', name: 'Central African Franc', locale: 'fr-FR', flag: '🌍' },
  { code: 'XOF', symbol: 'CFA', name: 'West African Franc', locale: 'fr-FR', flag: '🌍' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', locale: 'en-IN', flag: '🇮🇳' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', locale: 'pt-BR', flag: '🇧🇷' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso', locale: 'es-MX', flag: '🇲🇽' },
  { code: 'TND', symbol: 'DT', name: 'Tunisian Dinar', locale: 'fr-TN', flag: '🇹🇳' },
];

// Taux de change par défaut (par rapport à EUR) - fallback si l'API échoue
const DEFAULT_RATES = {
  EUR: 1, USD: 1.08, GBP: 0.85, CAD: 1.48, CHF: 0.96, JPY: 168.5,
  CNY: 7.85, AUD: 1.65, MAD: 10.85, XAF: 655.96, XOF: 655.96,
  INR: 91.5, BRL: 5.45, MXN: 19.5, TND: 3.35,
};

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currencyCode, setCurrencyCode] = useState(() => {
    return localStorage.getItem('currency') || 'CAD';
  });
  const [rates, setRates] = useState(DEFAULT_RATES);

  useEffect(() => {
    localStorage.setItem('currency', currencyCode);
  }, [currencyCode]);

  // Récupération des taux de change depuis exchangerate.host (gratuit, sans clé)
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/EUR');
        if (response.ok) {
          const data = await response.json();
          if (data.rates) {
            setRates({ EUR: 1, ...data.rates });
          }
        }
      } catch (err) {
        // Garde les taux par défaut en cas d'échec réseau
        console.warn('[CurrencyContext] exchange rates fetch failed, using defaults:', err);
      }
    };
    fetchRates();
  }, []);

  const currentCurrency = useMemo(
    () => CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0],
    [currencyCode]
  );

  // Convertit un montant EUR vers la devise courante
  const convertAmount = useCallback((amountInEur) => {
    const rate = rates[currencyCode] || 1;
    return amountInEur * rate;
  }, [rates, currencyCode]);

  // Convertit un montant exprimé dans `fromCurrency` vers la devise courante.
  // Toutes les conversions passent par EUR (les rates sont en base EUR).
  const convertFromCurrency = useCallback((amount, fromCurrency = 'EUR') => {
    if (amount == null || isNaN(amount)) return 0;
    const rateFrom = rates[fromCurrency] || 1;
    const rateTo = rates[currencyCode] || 1;
    if (rateFrom <= 0) return amount;
    const amountInEur = amount / rateFrom;
    return amountInEur * rateTo;
  }, [rates, currencyCode]);

  // Convertit un montant de la devise courante vers EUR (pour le stockage)
  const convertToEur = useCallback((amountInCurrentCurrency) => {
    const rate = rates[currencyCode] || 1;
    return rate > 0 ? amountInCurrentCurrency / rate : amountInCurrentCurrency;
  }, [rates, currencyCode]);

  // Convertit et formate un montant exprimé dans une devise source quelconque
  // vers la devise courante de l'utilisateur. Utile pour les prix d'abonnement
  // configurés en CAD côté PayPal mais affichés selon la devise choisie.
  const formatFromCurrency = useCallback((amount, fromCurrency = 'EUR') => {
    const rateFrom = rates[fromCurrency] || 1;
    const rateTo = rates[currencyCode] || 1;
    const amountInEur = rateFrom > 0 ? (amount || 0) / rateFrom : 0;
    const converted = amountInEur * rateTo;
    try {
      return new Intl.NumberFormat(currentCurrency.locale, {
        style: 'currency',
        currency: currentCurrency.code,
        maximumFractionDigits: 2,
      }).format(converted);
    } catch (e) {
      return `${converted.toFixed(2)} ${currentCurrency.symbol}`;
    }
  }, [rates, currencyCode, currentCurrency]);

  // Formate un montant (déjà en EUR) dans la devise courante
  const formatCurrency = useCallback((amount) => {
    const converted = convertAmount(amount || 0);
    try {
      return new Intl.NumberFormat(currentCurrency.locale, {
        style: 'currency',
        currency: currentCurrency.code,
        maximumFractionDigits: 2,
      }).format(converted);
    } catch (e) {
      return `${converted.toFixed(2)} ${currentCurrency.symbol}`;
    }
  }, [convertAmount, currentCurrency]);

  // Formate un montant exprimé dans une devise NATIVE (ex: CAD pour JEPI.TO,
  // USD pour AAPL) vers la devise courante de l'utilisateur, AVEC conversion
  // correcte via le taux de change réel.
  //
  // Important : `formatCurrency()` part du principe que le montant est déjà
  // en EUR et fait simplement EUR → user_currency. C'est faux pour les prix
  // marché Yahoo Finance qui sont dans la devise native de chaque bourse.
  // Cette fonction corrige ce problème en respectant la devise d'origine.
  const formatNativePrice = useCallback((amount, fromCurrency = 'USD') => {
    if (amount == null || isNaN(amount)) return '—';
    const converted = convertFromCurrency(amount, fromCurrency);
    try {
      return new Intl.NumberFormat(currentCurrency.locale, {
        style: 'currency',
        currency: currentCurrency.code,
        maximumFractionDigits: 2,
      }).format(converted);
    } catch (e) {
      return `${converted.toFixed(2)} ${currentCurrency.symbol}`;
    }
  }, [convertFromCurrency, currentCurrency]);

  const changeCurrency = useCallback((newCode) => {
    if (CURRENCIES.find(c => c.code === newCode)) {
      setCurrencyCode(newCode);
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      currencyCode,
      currentCurrency,
      changeCurrency,
      formatCurrency,
      formatFromCurrency,
      formatNativePrice,
      convertAmount,
      convertFromCurrency,
      convertToEur,
      rates,
      currencies: CURRENCIES,
    }),
    [currencyCode, currentCurrency, changeCurrency, formatCurrency, formatFromCurrency, formatNativePrice, convertAmount, convertFromCurrency, convertToEur, rates]
  );

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
};
